// src/receptionist/role.ts
//
// Two roles, resolved from the JWT in production. Outside production a
// dev-only override cookie makes both experiences testable before the backend
// emits them. Lifted from @oura/role, which anticipates the same BE contract.
//
// The role literals are load-bearing (ourteam-backend#4670). The merchant role
// is `receptionist-client`, not the earlier "-merchant" spelling: the backend
// stores it in `users.role`, which is VARCHAR(20), and that spelling was 21
// characters. `receptionist-client` is 19 and mirrors the `oura-client` /
// `oura-staff` pair.

import { useUserProfileContext } from "@context/user_profile";
import { getCookie, setCookie } from "@util/cookie";

export type ReceptionistRole = "receptionist-staff" | "receptionist-client";

export const RECEPTIONIST_ROLES: ReceptionistRole[] = [
    "receptionist-staff",
    "receptionist-client",
];

/** The workspace type a Receptionist merchant carries: a `companies` row with
 *  `type = "receptionist"`, the same shape Oura uses with `type = "oura"`. */
export const RECEPTIONIST_COMPANY_TYPE = "receptionist";

/** Fixture only: the merchant the demo config belongs to. Used as the fallback
 *  while the product runs on fixtures and the JWT carries no merchant. */
export const RECEPTIONIST_DEMO_MERCHANT_ID = "ownlab-secret-skin";

const DEV_ROLE_COOKIE = "__receptionist_dev_role__";

export function isReceptionistRole(role?: string | null): role is ReceptionistRole {
    return role === "receptionist-staff" || role === "receptionist-client";
}

function isPlatformAdmin(role?: string | null): boolean {
    return role === "admin" || role === "super_admin";
}

/** Production is the only environment where role gating is authoritative. */
export function isReceptionistProd(): boolean {
    return import.meta.env.VITE_APP_ENV === "production";
}

/**
 * Whether the product is exposed at all.
 *
 * Production is opt-IN: every surface stays hidden unless
 * `VITE_ENABLE_RECEPTIONIST` is exactly "true". That lets the code ship to
 * `main` dark and be switched on with an environment change instead of a
 * release, and keeps it off by default if the variable is ever dropped. Dev
 * and staging are always on; that is where it is built and QA'd.
 *
 * Deliberately separate from the role gate. Roles decide WHO may enter once
 * the product is live; this decides WHETHER it is live, so a role claim
 * appearing in a JWT earlier than expected cannot expose an unlaunched
 * product on its own.
 */
export function isReceptionistEnabled(): boolean {
    if (!isReceptionistProd()) return true;
    return import.meta.env.VITE_ENABLE_RECEPTIONIST === "true";
}

/**
 * Who may enter once the product is live. Wider than `isReceptionistRole` on
 * purpose: the API admits platform admins on every receptionist route
 * (ourteam-backend#4670), and a route guard stricter than the API it guards is
 * how Oura shipped a menu entry that bounced admins to /unauthorized the day
 * the product went live.
 */
export function mayEnterReceptionist(role?: string | null): boolean {
    return isReceptionistRole(role) || isPlatformAdmin(role);
}

export function setDevReceptionistRole(role: ReceptionistRole): void {
    setCookie(DEV_ROLE_COOKIE, role);
}

/**
 * The effective role: the real one from the session when the backend has
 * granted it, otherwise the dev override. Never returns a non-receptionist
 * role.
 *
 * A platform admin is STAFF in every environment, stated explicitly rather
 * than reached through the fallback, which defaults to the merchant view.
 * Outside production the dev switch still wins once set, because
 * impersonation is how the merchant experience is previewed.
 */
export function useReceptionistRole(): ReceptionistRole {
    const [, userInfo] = useUserProfileContext();
    const role = userInfo?.role;
    if (isReceptionistRole(role)) return role;
    // Outside production the dev switch wins when it has been set — that is
    // how the merchant experience is previewed. But an admin who has never
    // touched it is staff here too, not a merchant: the API admits them on
    // every route (ourteam-backend#4670) and the roll-up is what they came
    // for (#3611). Landing them on a single merchant's fixture instead read as
    // the product being broken.
    if (!isReceptionistProd()) {
        const dev = getCookie(DEV_ROLE_COOKIE);
        if (isReceptionistRole(dev)) return dev;
    }
    if (isPlatformAdmin(role)) return "receptionist-staff";
    return "receptionist-client";
}

/**
 * Which merchant a client session belongs to.
 *
 * `/auth/me` carries `receptionist: { merchantId, merchantName }` for a
 * `receptionist-client` and for nobody else — staff have no single merchant,
 * so for them this is null and the console loads a merchant from the URL
 * instead.
 *
 * The fixture check comes FIRST, and that ordering is the whole point. The
 * backend now sends a real merchant id, and the demo store has never heard of
 * it: `getMerchant("receptionist-one")` against the fixtures returns
 * `notFound`, so honouring the JWT while the fixtures are serving showed a
 * perfectly good session an empty console. While the demo dataset is on, the
 * demo merchant is the only one that exists.
 */
export function useReceptionistMerchantId(): string | null {
    const [, userInfo] = useUserProfileContext();
    // Same precedence as the service layer: the demo dataset wins over live.
    const mock = import.meta.env.VITE_RECEPTIONIST_MOCK_DATA === "true";
    const live = !mock && import.meta.env.VITE_ENABLE_RECEPTIONIST_LIVE_API === "true";
    if (!live) return RECEPTIONIST_DEMO_MERCHANT_ID;
    return userInfo?.receptionist?.merchantId ?? null;
}
